import React from 'react';
import { Box, Flex, Text, Skeleton, Portal, Divider, Avatar } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconLogoTypeSmall, IconChevron } from '../../../components/Icons';
import { useStockOwnerManagement } from './useStockOwnerManagement';
import { RouteEnum } from '../../../routes/routeEnum';
import type { StockOwnerRow } from './useStockOwnerManagement';
import './StockOwnerManagement.scss';

const SKELETON_COUNT = 2;

const StockOwnerLabel: React.FC<{ stockOwner: StockOwnerRow }> = ({
  stockOwner,
}) => {
  if (stockOwner.brandImageUrl) {
    return (
      <img
        src={stockOwner.brandImageUrl}
        alt={stockOwner.name}
        className='stock-owner-management__channel-logo-img'
      />
    );
  }
  return (
    <Avatar
      size='sm'
      name={stockOwner.name}
      className='stock-owner-management__so-avatar'
    />
  );
};

const StockOwnerManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    isOpen,
    isLoading,
    isSwitching,
    stockOwners,
    activeStockOwner,
    activeStockOwnerId,
    triggerRef,
    dropdownPosition,
    handleToggle,
    handleClose,
    handleSelectStockOwner,
  } = useStockOwnerManagement();

  if (stockOwners.length === 0) {
    return null;
  }

  return (
    <Box className='stock-owner-management'>
      <Flex
        ref={triggerRef}
        className={`stock-owner-management__trigger ${isOpen ? 'stock-owner-management__trigger--open' : ''}`}
        align='center'
        gap={2}
        onClick={handleToggle}
        cursor='pointer'
        role='button'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
      >
        {isLoading ? (
          <Skeleton height='24px' width='143px' borderRadius='4px' />
        ) : activeStockOwner ? (
          <Box className='stock-owner-management__channel-logo'>
            <StockOwnerLabel stockOwner={activeStockOwner} />
          </Box>
        ) : (
          <Box className='stock-owner-management__logo' flexShrink={0}>
            <IconLogoTypeSmall isRelative />
          </Box>
        )}

        <Box className='stock-owner-management__chevron'>
          <IconChevron isOpen={isOpen} isRelative />
        </Box>
      </Flex>

      {isOpen && (
        <Portal>
          <Box
            className='stock-owner-management__dropdown'
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 9999,
            }}
          >
            {(isLoading || stockOwners.length > 0) && (
              <>
                <Box className='stock-owner-management__list' role='listbox'>
                  {isLoading
                    ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                        <Flex
                          key={i}
                          className='stock-owner-management__item'
                          align='center'
                          gap={3}
                          pointerEvents='none'
                        >
                          <Skeleton height='14px' flex={1} borderRadius='4px' />
                        </Flex>
                      ))
                    : stockOwners.map((so: StockOwnerRow) => {
                        const isActive = so.id === activeStockOwnerId;
                        return (
                          <Flex
                            key={so.id}
                            className={`stock-owner-management__item ${isActive ? 'stock-owner-management__item--active' : ''}`}
                            align='center'
                            gap={3}
                            onClick={() => !isSwitching && handleSelectStockOwner(so)}
                            role='option'
                            aria-selected={isActive}
                          >
                            <Text
                              as='span'
                              className='stock-owner-management__item-label'
                              flex={1}
                            >
                              {so.name}
                            </Text>

                            {isActive && (
                              <Box
                                className='stock-owner-management__item-check'
                                color='blue.500'
                              >
                                <svg
                                  width={14}
                                  height={14}
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  stroke='currentColor'
                                  strokeWidth={2.5}
                                >
                                  <polyline points='20 6 9 17 4 12' />
                                </svg>
                              </Box>
                            )}
                          </Flex>
                        );
                      })}
                </Box>

                <Divider />
              </>
            )}

            <Box
              className='stock-owner-management__manage'
              onClick={() => {
                handleClose();
                navigate(RouteEnum.STOCK_OWNERS);
              }}
            >
              <Text as='span' className='stock-owner-management__manage-text'>
                {t('navigation.stockOwners')}
              </Text>
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default StockOwnerManagement;
